import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNotify, useTheme } from 'react-admin'
import { Box, Paper, Typography, IconButton, Tooltip, CircularProgress, Popover, Divider, Collapse, TextField } from '@mui/material'

import ProjectTimelineDialog from './ProjectTimelineDialog'
import { supabaseClient } from '../../supabase'

import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import TimelineIcon from '@mui/icons-material/Timeline'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'

const MONTH_NAMES = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
const ROW_HEIGHT = 36
const SUB_ROW_HEIGHT = 28
const HEADER_HEIGHT = 32

const PHASE_OPACITY = {
	développement: 1,
	tests: 0.8,
	préparation: 0.55,
	attente: 0.4,
	'tests ux': 0.8,
}

const getPhaseOpacity = (label) => {
	const lower = label.toLowerCase()
	for (const [key, opacity] of Object.entries(PHASE_OPACITY)) {
		if ( lower.includes(key) ) return opacity
	}
	return 1
}

const ProjectTimeline = ({ identity }) => {
	const [theme] = useTheme()
	const isDark = theme === 'dark'
	const notify = useNotify()

	const [projects, setProjects] = useState([])
	const [loading, setLoading] = useState(true)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [editingProject, setEditingProject] = useState(null)
	const [collapsed, setCollapsed] = useState(true)
	const [confirmDeleteId, setConfirmDeleteId] = useState(null)
	const [popoverAnchor, setPopoverAnchor] = useState(null)
	const [popoverProject, setPopoverProject] = useState(null)
	const [expandedProjects, setExpandedProjects] = useState(new Set())
	const [shortDevs, setShortDevs] = useState([])
	const [editingDev, setEditingDev] = useState(null)
	const [devForm, setDevForm] = useState({ title: '', description: '' })

	const chartRef = useRef(null)

	const isSuperAdmin = identity?.id === process.env.REACT_APP_SUPERADMIN_UUID

	const fetchShortDevs = useCallback(async () => {
		const { data, error } = await supabaseClient
			.from('short_developments')
			.select('*')
			.order('sort_order')

		if ( error ) {
			console.error(error)
		} else {
			setShortDevs(data || [])
		}
	}, [])

	const fetchProjects = useCallback(async () => {
		const { data, error } = await supabaseClient
			.from('project_timeline')
			.select('*, phases:project_timeline_phases(*)')
			.order('sort_order')

		if ( error ) {
			notify('Erreur lors du chargement de la timeline', { type: 'error' })
			console.error(error)
		} else {
			const sorted = (data || []).sort((a, b) => {
				const aMin = Math.min(...(a.phases || []).map(ph => new Date(ph.start_date)))
				const bMin = Math.min(...(b.phases || []).map(ph => new Date(ph.start_date)))
				return aMin - bMin
			})
			sorted.forEach(p => {
				if ( p.phases ) p.phases.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
			})
			setProjects(sorted)
		}
		setLoading(false)
	}, [notify])

	useEffect(() => { fetchProjects(); fetchShortDevs() }, [fetchProjects, fetchShortDevs])

	const recentProjects = useMemo(() => {
		const twoMonthsAgo = new Date()
		twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2)
		twoMonthsAgo.setHours(0, 0, 0, 0)
		return projects.filter(project => {
			const phases = project.phases || []
			if ( phases.length === 0 ) return false
			return phases.some(ph => new Date(ph.end_date) >= twoMonthsAgo)
		})
	}, [projects])

	const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
		const allDates = recentProjects.flatMap(p =>
			(p.phases || []).flatMap(ph => [new Date(ph.start_date), new Date(ph.end_date)])
		)
		if ( allDates.length === 0 ) {
			const now = new Date()
			return { timelineStart: now, timelineEnd: now, totalDays: 1 }
		}
		const min = new Date(Math.min(...allDates))
		const max = new Date(Math.max(...allDates))
		const start = new Date(min.getFullYear(), min.getMonth(), 1)
		const end = new Date(max.getFullYear(), max.getMonth() + 1, 0)
		const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
		return { timelineStart: start, timelineEnd: end, totalDays: days }
	}, [recentProjects])

	const months = useMemo(() => {
		const result = []
		const cursor = new Date(timelineStart)
		while (cursor <= timelineEnd) {
			const monthStart = new Date(cursor)
			const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)
			const daysInMonth = Math.min(
				Math.ceil((monthEnd - timelineStart) / (1000 * 60 * 60 * 24)) + 1,
				totalDays
			) - Math.max(
				Math.ceil((monthStart - timelineStart) / (1000 * 60 * 60 * 24)),
				0
			)
			result.push({
				label: `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`,
				days: daysInMonth,
			})
			cursor.setMonth(cursor.getMonth() + 1)
		}
		return result
	}, [timelineStart, timelineEnd, totalDays])

	const dayToPercent = useCallback((date) => {
		const d = new Date(date)
		const offset = (d - timelineStart) / (1000 * 60 * 60 * 24)
		return (offset / totalDays) * 100
	}, [timelineStart, totalDays])

	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const todayPercent = dayToPercent(today)
	const showToday = todayPercent >= 0 && todayPercent <= 100

	useEffect(() => {
		if ( chartRef.current && showToday && !loading ) {
			const container = chartRef.current
			const scrollTarget = (todayPercent / 100) * container.scrollWidth - 60
			container.scrollLeft = Math.max(0, scrollTarget)
		}
	}, [loading, showToday, todayPercent])

	const toggleExpand = (projectId) => {
		setExpandedProjects(prev => {
			const next = new Set(prev)
			if ( next.has(projectId) ) {
				next.delete(projectId)
			} else {
				next.add(projectId)
			}
			return next
		})
	}

	const handleDelete = async (projectId) => {
		const { error } = await supabaseClient
			.from('project_timeline')
			.delete()
			.eq('id', projectId)

		if ( error ) {
			notify('Erreur lors de la suppression', { type: 'error' })
		} else {
			notify('Projet supprimé', { type: 'success' })
			setConfirmDeleteId(null)
			fetchProjects()
		}
	}

	const handleEdit = (project) => {
		setEditingProject(project)
		setDialogOpen(true)
	}

	const handleCreate = () => {
		setEditingProject(null)
		setDialogOpen(true)
	}

	const handleDialogClose = () => {
		setDialogOpen(false)
		setEditingProject(null)
		fetchProjects()
	}

	const handleDevEdit = (dev) => {
		setEditingDev(dev.id)
		setDevForm({ title: dev.title, description: dev.description || '' })
	}

	const handleDevSave = async () => {
		if ( !devForm.title.trim() ) return

		if ( editingDev === 'new' ) {
			const { error } = await supabaseClient
				.from('short_developments')
				.insert({ title: devForm.title.trim(), description: devForm.description.trim() || null, sort_order: shortDevs.length })

			if ( error ) {
				notify('Erreur lors de la création', { type: 'error' })
			}
		} else {
			const { error } = await supabaseClient
				.from('short_developments')
				.update({ title: devForm.title.trim(), description: devForm.description.trim() || null })
				.eq('id', editingDev)

			if ( error ) {
				notify('Erreur lors de la mise à jour', { type: 'error' })
			}
		}

		setEditingDev(null)
		setDevForm({ title: '', description: '' })
		fetchShortDevs()
	}

	const handleDevDelete = async (id) => {
		const { error } = await supabaseClient
			.from('short_developments')
			.delete()
			.eq('id', id)

		if ( error ) {
			notify('Erreur lors de la suppression', { type: 'error' })
		}
		fetchShortDevs()
	}

	const handleDevCancel = () => {
		setEditingDev(null)
		setDevForm({ title: '', description: '' })
	}

	if ( loading ) {
		return (
			<Paper variant="outlined" sx={{ p: 3, mt: 2, display: 'flex', justifyContent: 'center' }}>
				<CircularProgress size={24} />
			</Paper>
		)
	}

	if ( projects.length === 0 && !isSuperAdmin ) return null

	const renderRows = []
	recentProjects.forEach(project => {
		const phases = project.phases || []
		const canExpand = phases.length > 1
		const isExpanded = canExpand && expandedProjects.has(project.id)
		const minDate = phases.length > 0 ? phases[0].start_date : null
		const maxDate = phases.length > 0 ? phases[phases.length - 1].end_date : null

		renderRows.push({
			type: 'project',
			project,
			isExpanded,
			canExpand,
			minDate,
			maxDate,
		})

		if ( isExpanded ) {
			phases.forEach((phase, index) => {
				renderRows.push({
					type: 'phase',
					project,
					phase,
					phaseIndex: index,
					totalPhases: phases.length,
				})
			})
		}
	})

	return (
		<>
			<Paper variant="outlined" sx={{ mt: 2, mb: 2, overflow: 'hidden' }}>
				<Box
					onClick={() => setCollapsed(prev => !prev)}
					sx={{
						position: 'relative',
						overflow: 'hidden',
						padding: '16px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						minHeight: '11vh',
						cursor: 'pointer',
						'&:before': {
							position: 'absolute',
							top: '50%',
							left: 0,
							display: 'block',
							content: `''`,
							height: '200%',
							aspectRatio: '1',
							transform: 'translate(-30%, -60%)',
							borderRadius: '50%',
							backgroundColor: 'primary.main',
							opacity: 0.15,
						},
					}}
				>
					<Box width="3em" sx={{ color: '#57aaa6' }}>
						<TimelineIcon fontSize="large" />
					</Box>

					<Box textAlign="right">
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
							{isSuperAdmin && (
								<Tooltip title="Ajouter un projet">
									<IconButton
										size="small"
										onClick={(e) => { e.stopPropagation(); handleCreate() }}
										sx={{ color: isDark ? '#81c784' : '#4caf50' }}
									>
										<AddIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}

							<Typography variant="body1" color="textSecondary">Timeline des projets 2026</Typography>

							<ExpandMoreIcon
								sx={{
									color: 'text.secondary',
									transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
									transition: 'transform 0.3s',
								}}
							/>
						</Box>

						<Typography variant="h5" component="h2">{projects.length}</Typography>
					</Box>
				</Box>

				<Collapse in={!collapsed}>
					<Divider />

					<Box sx={{ display: 'flex', gap: 0 }}>
						{/* Left 60% — Timeline */}
						<Box sx={{ width: '60%', p: 2, borderRight: `1px solid ${isDark ? '#333' : '#eee'}` }}>
							<Box
								ref={chartRef}
								sx={{
									overflowX: 'auto',
									overflowY: 'hidden',
									position: 'relative',
								}}
							>
								<Box sx={{ minWidth: Math.max(600, totalDays * 12), position: 'relative' }}>
									{/* Month headers */}
									<Box sx={{ display: 'flex', height: HEADER_HEIGHT, borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}` }}>
										{months.map((month, i) => (
											<Box
												key={i}
												sx={{
													width: `${(month.days / totalDays) * 100}%`,
													textAlign: 'center',
													borderRight: `1px solid ${isDark ? '#444' : '#ddd'}`,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
												}}
											>
												<Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#ccc' : '#555' }}>
													{month.label}
												</Typography>
											</Box>
										))}
									</Box>

									{/* Rows */}
									{renderRows.map((row) => {
										if ( row.type === 'project' ) {
											return (
												<ProjectRow
													key={`row-${row.project.id}`}
													row={row}
													dayToPercent={dayToPercent}
													isDark={isDark}
													months={months}
													totalDays={totalDays}
													onToggle={() => toggleExpand(row.project.id)}
													isSuperAdmin={isSuperAdmin}
													confirmDeleteId={confirmDeleteId}
													onEdit={() => handleEdit(row.project)}
													onDeleteRequest={() => setConfirmDeleteId(row.project.id)}
													onDeleteConfirm={() => handleDelete(row.project.id)}
													onShowDescription={(e) => {
														setPopoverAnchor({ top: e.clientY, left: e.clientX })
														setPopoverProject(row.project)
													}}
												/>
											)
										}

										return (
											<PhaseRow
												key={`row-phase-${row.phase.id}`}
												row={row}
												dayToPercent={dayToPercent}
												isDark={isDark}
												months={months}
												totalDays={totalDays}
											/>
										)
									})}

									{/* SVG overlay for diagonal connectors */}
									<ConnectorOverlay
										renderRows={renderRows}
										dayToPercent={dayToPercent}
									/>

									{/* Today marker */}
									{showToday && (
										<Box
											sx={{
												position: 'absolute',
												left: `${todayPercent}%`,
												top: 0,
												bottom: 0,
												width: '2px',
												backgroundColor: '#f44336',
												zIndex: 2,
												pointerEvents: 'none',
											}}
										>
											<Box
												sx={{
													position: 'absolute',
													top: 0,
													left: -16,
													backgroundColor: '#f44336',
													color: '#fff',
													fontSize: '0.6rem',
													fontWeight: 600,
													px: 0.5,
													borderRadius: '0 0 3px 3px',
												}}
											>
												Auj.
											</Box>
										</Box>
									)}
								</Box>
							</Box>

							{/* Légende par projet */}
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5, pt: 1, borderTop: `1px solid ${isDark ? '#333' : '#eee'}` }}>
								{recentProjects.map(project => (
									<Box key={project.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: project.color }} />

										<Typography variant="caption" sx={{ fontSize: '0.65rem', color: isDark ? '#aaa' : '#777' }}>
											{project.name}
										</Typography>
									</Box>
								))}
							</Box>
						</Box>

						{/* Right 40% — Short developments */}
						<Box sx={{ width: '40%', p: 2 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									<BuildCircleIcon sx={{ fontSize: 16, color: isDark ? '#aaa' : '#888' }} />

									<Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: isDark ? '#ccc' : '#555' }}>
										Développements courts
									</Typography>
								</Box>

								{isSuperAdmin && editingDev !== 'new' && (
									<IconButton
										size="small"
										onClick={() => { setEditingDev('new'); setDevForm({ title: '', description: '' }) }}
										sx={{ color: isDark ? '#81c784' : '#4caf50', p: 0.25 }}
									>
										<AddIcon fontSize="small" />
									</IconButton>
								)}
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
								{shortDevs.map(dev => (
									<Box
										key={dev.id}
										sx={{
											p: 1,
											borderRadius: '6px',
											backgroundColor: isDark ? '#1e1e1e' : '#f9f9f9',
											border: `1px solid ${isDark ? '#333' : '#eee'}`,
											'&:hover .dev-controls': { opacity: 1 },
											position: 'relative',
										}}
									>
										{editingDev === dev.id ? (
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
												<TextField
													size="small"
													value={devForm.title}
													onChange={(e) => setDevForm(prev => ({ ...prev, title: e.target.value }))}
													placeholder="Titre"
													autoFocus
													sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 } }}
												/>

												<TextField
													size="small"
													value={devForm.description}
													onChange={(e) => setDevForm(prev => ({ ...prev, description: e.target.value }))}
													placeholder="Description"
													multiline
													maxRows={3}
													sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
												/>

												<Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
													<IconButton size="small" onClick={handleDevCancel} sx={{ p: 0.25 }}>
														<CloseIcon sx={{ fontSize: 14 }} />
													</IconButton>

													<IconButton size="small" onClick={handleDevSave} sx={{ p: 0.25, color: isDark ? '#81c784' : '#4caf50' }}>
														<SaveIcon sx={{ fontSize: 14 }} />
													</IconButton>
												</Box>
											</Box>
										) : (
											<>
												<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}>
													{dev.title}
												</Typography>

												{dev.description && (
													<Typography variant="caption" sx={{ color: isDark ? '#999' : '#777', fontSize: '0.7rem', lineHeight: 1.3 }}>
														{dev.description}
													</Typography>
												)}

												{isSuperAdmin && (
													<Box
														className="dev-controls"
														sx={{
															position: 'absolute',
															top: 4,
															right: 4,
															display: 'flex',
															gap: 0.25,
															opacity: 0,
															transition: 'opacity 0.15s',
															backgroundColor: isDark ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)',
															borderRadius: '4px',
															px: 0.25,
														}}
													>
														<IconButton size="small" onClick={() => handleDevEdit(dev)} sx={{ p: 0.25 }}>
															<EditIcon sx={{ fontSize: 12, color: isDark ? '#90caf9' : '#1976d2' }} />
														</IconButton>

														<IconButton size="small" onClick={() => handleDevDelete(dev.id)} sx={{ p: 0.25 }}>
															<CloseIcon sx={{ fontSize: 12, color: isDark ? '#999' : '#bbb' }} />
														</IconButton>
													</Box>
												)}
											</>
										)}
									</Box>
								))}

								{/* Inline new form */}
								{editingDev === 'new' && (
									<Box
										sx={{
											p: 1,
											borderRadius: '6px',
											border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
											display: 'flex',
											flexDirection: 'column',
											gap: 0.5,
										}}
									>
										<TextField
											size="small"
											value={devForm.title}
											onChange={(e) => setDevForm(prev => ({ ...prev, title: e.target.value }))}
											placeholder="Titre"
											autoFocus
											sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 } }}
										/>

										<TextField
											size="small"
											value={devForm.description}
											onChange={(e) => setDevForm(prev => ({ ...prev, description: e.target.value }))}
											placeholder="Description"
											multiline
											maxRows={3}
											sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
										/>

										<Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
											<IconButton size="small" onClick={handleDevCancel} sx={{ p: 0.25 }}>
												<CloseIcon sx={{ fontSize: 14 }} />
											</IconButton>

											<IconButton size="small" onClick={handleDevSave} sx={{ p: 0.25, color: isDark ? '#81c784' : '#4caf50' }}>
												<SaveIcon sx={{ fontSize: 14 }} />
											</IconButton>
										</Box>
									</Box>
								)}

								{shortDevs.length === 0 && editingDev !== 'new' && (
									<Typography variant="caption" sx={{ color: isDark ? '#666' : '#bbb', textAlign: 'center', py: 2 }}>
										Aucun développement court
									</Typography>
								)}
							</Box>
						</Box>
					</Box>
				</Collapse>
			</Paper>

			<Popover
				open={Boolean(popoverAnchor)}
				onClose={() => { setPopoverAnchor(null); setPopoverProject(null) }}
				anchorReference="anchorPosition"
				anchorPosition={popoverAnchor}
			>
				<Box sx={{ p: 2, maxWidth: 320 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
						{popoverProject?.name}
					</Typography>

					<Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
						{popoverProject?.description}
					</Typography>
				</Box>
			</Popover>

			<ProjectTimelineDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				project={editingProject}
				maxSortOrder={projects.length > 0 ? Math.max(...projects.map(p => p.sort_order)) : -1}
			/>
		</>
	)
}

// ==================
// Project row — collapsed: solid bar with name. Expanded: clickable row to collapse.
// ==================

const ProjectRow = ({
	row, dayToPercent, isDark, months, totalDays, onToggle,
	isSuperAdmin, confirmDeleteId, onEdit, onDeleteRequest, onDeleteConfirm, onShowDescription,
}) => {
	const { project, isExpanded, canExpand, minDate, maxDate } = row

	return (
		<Box
			sx={{
				height: ROW_HEIGHT,
				position: 'relative',
				borderBottom: `1px solid ${isDark ? '#333' : '#eee'}`,
				cursor: canExpand ? 'pointer' : 'default',
				'&:hover .admin-controls': { opacity: 1 },
			}}
			onClick={canExpand ? onToggle : undefined}
		>
			<MonthGridLines months={months} totalDays={totalDays} isDark={isDark} />

			{/* Collapsed: solid bar */}
			{!isExpanded && minDate && maxDate && (
				<Tooltip
					title={`${project.name}: ${new Date(minDate).toLocaleDateString('fr-FR')} - ${new Date(maxDate).toLocaleDateString('fr-FR')}`}
					arrow
				>
					<Box
						sx={{
							position: 'absolute',
							left: `${dayToPercent(minDate)}%`,
							width: `${dayToPercent(maxDate) - dayToPercent(minDate)}%`,
							top: 6,
							height: ROW_HEIGHT - 12,
							backgroundColor: project.color,
							borderRadius: '4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							overflow: 'hidden',
							'&:hover': { filter: 'brightness(1.1)' },
							transition: 'filter 0.15s',
						}}
					>
						<Typography
							variant="caption"
							sx={{
								color: '#fff',
								fontSize: '0.6rem',
								fontWeight: 600,
								textShadow: '0 1px 2px rgba(0,0,0,0.3)',
								whiteSpace: 'nowrap',
								px: 0.5,
							}}
						>
							{(dayToPercent(maxDate) - dayToPercent(minDate)) > 5 ? project.name : ''}
						</Typography>
					</Box>
				</Tooltip>
			)}

			{/* Expanded: small project name label at left edge */}
			{isExpanded && (
				<Box
					sx={{
						position: 'absolute',
						left: 8,
						top: 0,
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						zIndex: 1,
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							backgroundColor: project.color,
							flexShrink: 0,
						}}
					/>

					<Typography
						variant="caption"
						sx={{
							fontSize: '0.65rem',
							fontWeight: 600,
							color: isDark ? '#ccc' : '#555',
							whiteSpace: 'nowrap',
						}}
					>
						{project.name}
					</Typography>
				</Box>
			)}

			{/* Admin controls — appear on hover, positioned at right edge */}
			{(isSuperAdmin || project.description) && (
				<Box
					className="admin-controls"
					sx={{
						position: 'absolute',
						right: 4,
						top: 0,
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						gap: 0.25,
						opacity: 0,
						transition: 'opacity 0.15s',
						zIndex: 3,
						backgroundColor: isDark ? 'rgba(30,30,30,0.85)' : 'rgba(255,255,255,0.85)',
						borderRadius: '4px',
						px: 0.5,
					}}
					onClick={(e) => e.stopPropagation()}
				>
					{project.description && (
						<IconButton size="small" onClick={onShowDescription} sx={{ p: 0.25 }}>
							<ManageSearchIcon sx={{ fontSize: 16, color: isDark ? '#aaa' : '#888' }} />
						</IconButton>
					)}

					{isSuperAdmin && (
						<>
							<IconButton size="small" onClick={onEdit} sx={{ p: 0.25 }}>
								<EditIcon sx={{ fontSize: 14, color: isDark ? '#90caf9' : '#1976d2' }} />
							</IconButton>

							{confirmDeleteId === project.id ? (
								<Tooltip title="Confirmer la suppression">
									<IconButton size="small" onClick={onDeleteConfirm} sx={{ p: 0.25 }}>
										<CloseIcon sx={{ fontSize: 14, color: '#f44336' }} />
									</IconButton>
								</Tooltip>
							) : (
								<IconButton size="small" onClick={onDeleteRequest} sx={{ p: 0.25 }}>
									<CloseIcon sx={{ fontSize: 14, color: isDark ? '#999' : '#bbb' }} />
								</IconButton>
							)}
						</>
					)}
				</Box>
			)}
		</Box>
	)
}

// ==================
// Phase sub-row (expanded)
// ==================

const PhaseRow = ({ row, dayToPercent, isDark, months, totalDays }) => {
	const { project, phase } = row
	const left = dayToPercent(phase.start_date)
	const right = dayToPercent(phase.end_date)
	const width = right - left
	const opacity = getPhaseOpacity(phase.label)

	return (
		<Box
			sx={{
				height: SUB_ROW_HEIGHT,
				position: 'relative',
				borderBottom: `1px solid ${isDark ? '#2a2a2a' : '#f5f5f5'}`,
			}}
		>
			<MonthGridLines months={months} totalDays={totalDays} isDark={isDark} light />

			<Tooltip
				title={`${phase.label}: ${new Date(phase.start_date).toLocaleDateString('fr-FR')} - ${new Date(phase.end_date).toLocaleDateString('fr-FR')}`}
				arrow
			>
				<Box
					sx={{
						position: 'absolute',
						left: `${left}%`,
						width: `${width}%`,
						top: 4,
						height: SUB_ROW_HEIGHT - 8,
						backgroundColor: project.color,
						opacity,
						borderRadius: '3px',
						cursor: 'default',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						overflow: 'hidden',
					}}
				>
					<Typography
						variant="caption"
						sx={{
							color: '#fff',
							fontSize: '0.55rem',
							fontWeight: 600,
							textShadow: '0 1px 2px rgba(0,0,0,0.3)',
							whiteSpace: 'nowrap',
							px: 0.5,
						}}
					>
						{width > 4 ? phase.label : ''}
					</Typography>
				</Box>
			</Tooltip>
		</Box>
	)
}

// ==================
// Month grid lines (reused)
// ==================

const MonthGridLines = ({ months, totalDays, isDark, light }) => (
	<>
		{months.reduce((acc, month, i) => {
			const prevDays = months.slice(0, i).reduce((s, m) => s + m.days, 0)
			const pct = ((prevDays + month.days) / totalDays) * 100
			if ( i < months.length - 1 ) {
				acc.push(
					<Box
						key={`grid-${i}`}
						sx={{
							position: 'absolute',
							left: `${pct}%`,
							top: 0,
							bottom: 0,
							width: '1px',
							backgroundColor: isDark
								? (light ? '#2a2a2a' : '#333')
								: (light ? '#f5f5f5' : '#eee'),
						}}
					/>
				)
			}
			return acc
		}, [])}
	</>
)

// ==================
// SVG connector overlay for diagonal lines between phases
// ==================

const ConnectorOverlay = ({ renderRows, dayToPercent }) => {
	const connectors = []
	let yOffset = 0

	for (let i = 0; i < renderRows.length; i++) {
		const row = renderRows[i]
		if ( row.type === 'project' ) {
			yOffset += ROW_HEIGHT
			continue
		}

		const nextRow = renderRows[i + 1]
		if ( nextRow && nextRow.type === 'phase' && nextRow.project.id === row.project.id ) {
			connectors.push({
				fromEndPercent: dayToPercent(row.phase.end_date),
				toStartPercent: dayToPercent(nextRow.phase.start_date),
				y1: yOffset + SUB_ROW_HEIGHT / 2,
				y2: yOffset + SUB_ROW_HEIGHT + SUB_ROW_HEIGHT / 2,
				color: row.project.color,
			})
		}
		yOffset += SUB_ROW_HEIGHT
	}

	if ( connectors.length === 0 ) return null

	const totalHeight = getRowsHeight(renderRows)

	return (
		<svg
			style={{
				position: 'absolute',
				top: HEADER_HEIGHT,
				left: 0,
				width: '100%',
				height: totalHeight,
				pointerEvents: 'none',
				overflow: 'visible',
			}}
		>
			{connectors.map((c, i) => (
				<line
					key={i}
					x1={`${c.fromEndPercent}%`}
					y1={c.y1}
					x2={`${c.toStartPercent}%`}
					y2={c.y2}
					stroke={c.color}
					strokeOpacity={0.4}
					strokeWidth={1.5}
					strokeDasharray="4 2"
				/>
			))}
		</svg>
	)
}

const getRowsHeight = (renderRows) => {
	let h = 0
	renderRows.forEach(row => {
		h += row.type === 'project' ? ROW_HEIGHT : SUB_ROW_HEIGHT
	})
	return h
}

export default ProjectTimeline
